import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  Clock,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  XCircle,
} from "lucide-react";
import type {
  CfoFundingLine,
  CfoFundingProgress,
  CfoFundingReceipt,
  CfoFundingReview,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { EVALUATION, diagnosticLabel, isSettled, progressOf, reasonLabel } from "./fundingLabels";
import { FundingProgressPanel } from "./FundingProgressPanel";
import { RUNNING_LABEL, currentOf, failedOf } from "./fundingProgress";

type Action = "pause" | "resume" | "cancel";

function Counter({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className={cn("text-lg font-semibold tabular-nums", tone)}>{value}</p>
    </div>
  );
}

const BUSINESS_FIELDS = [
  "Funding Type",
  "Announced Date",
  "Money Raised",
  "Money Raised Currency",
  "Money Raised (in USD)",
  "Funding Stage",
];

/** What a group needing a person proposes, shown field by field. */
function Differences({
  differences,
  reference,
  onReference,
}: {
  differences: Record<string, unknown>;
  reference: string;
  onReference: (value: string) => void;
}) {
  const candidates = Array.isArray(differences.reference_candidates)
    ? (differences.reference_candidates as string[])
    : null;
  const diagnostics = Array.isArray(differences.diagnostics)
    ? (differences.diagnostics as Record<string, string>[])
    : null;
  if (candidates) {
    return (
      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-slate-700">
          Which round is the reference round?
        </legend>
        {candidates.map((url) => (
          <label key={url} className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="radio"
              name="reference-round"
              checked={reference === url}
              onChange={() => onReference(url)}
            />
            <span className="truncate">{url.replace(/^https:\/\/www\.crunchbase\.com\//, "")}</span>
          </label>
        ))}
      </fieldset>
    );
  }
  if (diagnostics) {
    return (
      <div className="flex flex-wrap gap-1">
        {diagnostics.map((d, i) => (
          <span key={i} className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800">
            {diagnosticLabel(d)}
          </span>
        ))}
      </div>
    );
  }
  const rounds = Object.entries(differences).filter(
    ([, v]) => v && typeof v === "object" && "proposed" in (v as object),
  ) as [string, { before?: Record<string, unknown>; proposed?: Record<string, unknown> }][];
  if (!rounds.length) return null;
  return (
    <div className="space-y-2">
      {rounds.map(([ref, change]) => (
        <div key={ref} className="overflow-hidden rounded-lg border border-slate-200">
          <p className="truncate bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
            {ref.replace(/^https:\/\/www\.crunchbase\.com\//, "")}
          </p>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Field</th>
                <th className="px-2 py-1 text-left font-medium">Recorded</th>
                <th className="px-2 py-1 text-left font-medium">In the file</th>
              </tr>
            </thead>
            <tbody>
              {BUSINESS_FIELDS.filter(
                (f) => (change.before ?? {})[f] !== undefined || (change.proposed ?? {})[f] !== undefined,
              ).map((field) => {
                const before = (change.before ?? {})[field];
                const proposed = (change.proposed ?? {})[field];
                const changed = String(before ?? "") !== String(proposed ?? "");
                return (
                  <tr key={field} className={cn(changed && "bg-amber-50/60")}>
                    <td className="px-2 py-1 text-slate-500">{field}</td>
                    <td className="px-2 py-1 text-slate-700">{String(before ?? "—")}</td>
                    <td className={cn("px-2 py-1", changed ? "font-medium text-amber-900" : "text-slate-700")}>
                      {proposed === null ? "(empty)" : String(proposed ?? "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function GroupReview({
  line,
  revision,
  busy,
  onSubmit,
  onClose,
}: {
  line: CfoFundingLine;
  revision: number;
  busy: boolean;
  onSubmit: (request: CfoFundingReview) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [reference, setReference] = useState("");
  const [clear, setClear] = useState(false);
  const code = String(line.result.reason ?? "");
  const expected = line.result.expected_round_revisions;
  const revisions: Record<string, number> = {};
  if (expected && typeof expected === "object") {
    for (const [key, value] of Object.entries(expected)) if (typeof value === "number") revisions[key] = value;
  }
  const differences = (line.result.differences ?? {}) as Record<string, unknown>;
  const ready = reason.trim().length >= 3 && evidence.trim().length > 0;
  function submit(action: "accept" | "reject") {
    onSubmit({
      expected_revision: revision,
      group_key: line.group_key,
      action,
      reason: reason.trim(),
      evidence_refs: [evidence.trim()],
      expected_round_revisions: revisions,
      reference_round: reference || null,
      accept_empty_fields: clear,
    });
  }
  return (
    <div className="mt-2 space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <Differences differences={differences} reference={reference} onReference={setReference} />
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="space-y-1 text-xs text-slate-600">
          <span className="font-medium">Why</span>
          <Textarea
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Checked on the company's announcement"
          />
        </label>
        <label className="space-y-1 text-xs text-slate-600">
          <span className="font-medium">Source</span>
          <Input
            maxLength={255}
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="Link or reference of the proof"
          />
        </label>
      </div>
      {code === "EXPLICIT_CLEAR_REQUIRED" && (
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={clear} onChange={(e) => setClear(e.target.checked)} />
          Accept the emptied values of the file
        </label>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="outline" size="sm" disabled={busy || !ready} onClick={() => submit("reject")}>
          Reject the file's values
        </Button>
        <Button variant="primary" size="sm" disabled={busy || !ready} onClick={() => submit("accept")}>
          Accept and record
        </Button>
      </div>
    </div>
  );
}

/** Step 3: the import runs in the background; this follows it, step by step once the
 *  progress is read (rounds, collection, provider API calls, scoring, cohort). */
export function FundingImportStep({
  receipt,
  progress,
  lines,
  canConfirm,
  busy,
  error,
  onControl,
  onReview,
  onNewImport,
  onClose,
}: {
  receipt: CfoFundingReceipt;
  progress: CfoFundingProgress | null;
  lines: CfoFundingLine[] | null;
  canConfirm: boolean;
  busy: boolean;
  error: string | null;
  onControl: (action: Action) => void;
  onReview: (request: CfoFundingReview) => void;
  onNewImport: () => void;
  onClose: () => void;
}) {
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const rounds = progressOf(receipt);
  const settled = isSettled(receipt);
  const cancelled = receipt.state === "CANCELLED";
  const evaluation = EVALUATION[receipt.local_status] ?? {
    label: receipt.local_status.replace(/_/g, " ").toLowerCase(),
    tone: "wait" as const,
  };
  // The rounds are recorded, and the local work that follows (scoring, cohort) runs.
  const evaluating = settled && (progress ? !progress.complete : evaluation.tone === "busy");
  const current = progress ? currentOf(progress) : null;
  const failure = progress ? failedOf(progress) : null;
  const toReview = useMemo(() => {
    const groups = new Map<string, CfoFundingLine>();
    for (const line of lines ?? []) {
      if (["REVIEW", "FAILED"].includes(String(line.result.group_state)) && !groups.has(line.group_key)) {
        groups.set(line.group_key, line);
      }
    }
    return [...groups.values()];
  }, [lines]);

  const title = cancelled
    ? "Import cancelled"
    : !settled
      ? receipt.paused
        ? "Import paused"
        : "Importing your file…"
      : toReview.length
        ? `Import finished: ${toReview.length} ${toReview.length === 1 ? "company needs" : "companies need"} a review`
        : evaluating
          ? current
            ? `Rounds recorded. ${RUNNING_LABEL[current.key]}…`
            : "Rounds recorded, re-scoring the companies…"
          : failure
            ? `Import complete, but a step failed: ${failure.label.toLowerCase()}`
            : "Import complete";
  const Icon = cancelled
    ? CircleSlash
    : !settled
      ? receipt.paused
        ? Pause
        : Loader2
      : toReview.length
        ? AlertTriangle
        : evaluating
          ? Loader2
          : failure
            ? AlertTriangle
            : CheckCircle2;
  const iconTone = cancelled
    ? "bg-slate-100 text-slate-500"
    : (toReview.length || (failure && !evaluating)) && settled
      ? "bg-amber-50 text-amber-600"
      : settled && !evaluating
        ? "bg-emerald-50 text-emerald-600"
        : "bg-indigo-50 text-indigo-600";
  const running = !settled && !receipt.paused && !cancelled;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
        <div className="flex items-center gap-4">
          <span className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full", iconTone)}>
            <Icon className={cn("h-6 w-6", (running || evaluating) && Icon === Loader2 && "animate-spin")} />
          </span>
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900">{title}</p>
            <p className="truncate text-sm text-slate-500">{receipt.filename}</p>
          </div>
        </div>

        {progress ? (
          <FundingProgressPanel progress={progress} />
        ) : (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-slate-800">Recording the funding rounds</span>
                <span className="tabular-nums text-slate-500">
                  {rounds.done} / {rounds.total} companies · {Math.round(rounds.pct * 100)}%
                </span>
              </div>
              <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-700",
                    cancelled ? "bg-slate-300" : "bg-indigo-600",
                    running && "animate-pulse",
                  )}
                  style={{ width: `${Math.max(rounds.pct * 100, running ? 3 : 0)}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {evaluation.tone === "busy" ? (
                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
              ) : evaluation.tone === "done" ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : evaluation.tone === "fail" ? (
                <XCircle className="h-4 w-4 text-rose-600" />
              ) : (
                <Clock className="h-4 w-4 text-slate-400" />
              )}
              <span className="text-slate-700">Re-scoring the companies:</span>
              <span className="text-slate-500">{evaluation.label}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Counter label="Created" value={receipt.counters.created ?? 0} tone="text-indigo-600" />
          <Counter label="Updated" value={receipt.counters.updated ?? 0} tone="text-indigo-600" />
          <Counter
            label="Unchanged"
            value={(receipt.counters.unchanged ?? 0) + (receipt.counters.stale_replay ?? 0)}
            tone="text-slate-700"
          />
          <Counter label="To review" value={receipt.counters.needs_review ?? 0} tone="text-amber-600" />
          <Counter label="Rejected" value={receipt.counters.rejected ?? 0} tone="text-rose-600" />
        </div>

        {toReview.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Needs a review
            </h3>
            <ul className="space-y-2">
              {toReview.map((line) => (
                <li key={line.group_key} className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{line.organization_name}</p>
                      <p className="text-xs text-amber-800">{reasonLabel(line.result.reason)}</p>
                    </div>
                    {canConfirm && reviewing !== line.group_key && (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => setReviewing(line.group_key)}>
                        Review
                      </Button>
                    )}
                  </div>
                  {reviewing === line.group_key && (
                    <GroupReview
                      key={`${line.group_key}-${receipt.revision}`}
                      line={line}
                      revision={receipt.revision}
                      busy={busy}
                      onSubmit={(request) => {
                        onReview(request);
                        setReviewing(null);
                      }}
                      onClose={() => setReviewing(null)}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div role="alert" className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {cancelling && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <span>Cancel the companies not recorded yet? Recorded ones are kept.</span>
            <span className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCancelling(false)}>
                Keep importing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => {
                  onControl("cancel");
                  setCancelling(false);
                }}
              >
                Cancel them
              </Button>
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
        <div className="flex gap-2">
          {canConfirm && !settled && !cancelled && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onControl(receipt.paused ? "resume" : "pause")}
              >
                {receipt.paused ? <Play /> : <Pause />}
                {receipt.paused ? "Resume" : "Pause"}
              </Button>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setCancelling(true)}>
                Cancel remaining
              </Button>
            </>
          )}
          {settled && (
            <Button variant="outline" size="sm" onClick={onNewImport}>
              <RotateCcw />
              Import another file
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {(!settled || evaluating) && (
            <p className="hidden text-[11px] text-slate-500 sm:block">
              You can close this window: the import continues, its progress stays at the bottom
              right.
            </p>
          )}
          <Button variant={settled && !evaluating ? "primary" : "outline"} onClick={onClose}>
            {settled && !evaluating ? "Done" : "Continue in the background"}
          </Button>
        </div>
      </div>
    </div>
  );
}
